(() => {
  if (customElements.get('lyma-lamp-swing')) return;

  const profiles = {
  "malam": {
    "delay": 40,
    "frequency": 3.35,
    "decay": 0.72,
    "strength": 0.91,
    "limit": 6.0
  },
  "berlian": {
    "delay": 170,
    "frequency": 3.6,
    "decay": 0.83,
    "strength": 0.84,
    "limit": 5.5
  },
  "sekitar": {
    "delay": 290,
    "frequency": 2.85,
    "decay": 0.57,
    "strength": 0.78,
    "limit": 5.2
  },
  "awan-large": {
    "delay": 95,
    "frequency": 2.95,
    "decay": 0.6,
    "strength": 1,
    "limit": 6.2
  },
  "awan": {
    "delay": 240,
    "frequency": 3.2,
    "decay": 0.67,
    "strength": 0.88,
    "limit": 5.8
  },
  "alam": {
    "delay": 350,
    "frequency": 3.05,
    "decay": 0.76,
    "strength": 0.82,
    "limit": 5.4
  },
  "makan": {
    "delay": 120,
    "frequency": 3.45,
    "decay": 0.64,
    "strength": 0.96,
    "limit": 6.1
  },
  "bawang": {
    "delay": 410,
    "frequency": 3.15,
    "decay": 0.79,
    "strength": 0.86,
    "limit": 5.6
  },
  "umbi": {
    "delay": 205,
    "frequency": 2.75,
    "decay": 0.62,
    "strength": 0.93,
    "limit": 5.9
  }
};

  class LymaLampSwing extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.profile = profiles[this.getAttribute('data-lamp')] || profiles.malam;
      this.pendingVelocity = 0;
      this.kickTime = null;
      this.angle = 0;
      this.velocity = 0;
      this.frame = 0;
      this.visible = false;
      this.lastScroll = window.scrollY;
      this.tick = this.tick.bind(this);
      this.pendulum = this.querySelector('.lyma-lamp-swing__pendulum');
      this.lastTransform = '';
      const img = this.querySelector('img');
      if (!img) return;
      const ready = () => {
        if (!img.naturalWidth) return;
        const source = img.currentSrc || img.src;
        this.querySelectorAll('[data-cord-source]').forEach(piece => {
          if (piece.getAttribute('href') !== source) piece.setAttribute('href', source);
        });
        this.setAttribute('data-ready', '');
      };
      img.addEventListener('load', ready, { signal });
      img.addEventListener('error', () => { this.removeAttribute('data-ready'); this.stop(); }, { signal });
      if (img.complete) ready();
      this.motion.addEventListener('change', () => this.stop(), { signal });
      document.addEventListener('visibilitychange', () => {
        this.lastScroll = window.scrollY;
        if (document.hidden) this.stop();
      }, { signal });
      window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.lastScroll = window.scrollY;
          if (!this.visible) this.stop();
        }, { threshold: 0.05 });
        this.observer.observe(this);
      } else {
        this.visible = true;
      }
    }

    onScroll() {
      const delta = window.scrollY - this.lastScroll;
      this.lastScroll = window.scrollY;
      if (!delta || !this.visible || this.motion.matches || document.hidden || !this.hasAttribute('data-ready')) return;
      if (!this.observer) {
        const rect = this.getBoundingClientRect();
        if (!rect.width || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      }
      const maxVelocity = (this.mobile.matches ? 16 : 23) * this.profile.strength;
      this.pendingVelocity = Math.max(-maxVelocity, Math.min(maxVelocity, this.pendingVelocity + Math.max(-120, Math.min(120, delta)) * 0.09 * this.profile.strength));
      if (this.kickTime === null) this.kickTime = performance.now() + this.profile.delay;
      if (!this.frame) {
        this.previousTime = performance.now();
        this.setAttribute('data-moving', '');
        this.frame = requestAnimationFrame(this.tick);
      }
    }

    tick(now) {
      const dt = Math.min((now - this.previousTime) / 1000, 0.05);
      this.previousTime = now;
      if (this.kickTime !== null && now >= this.kickTime) {
        const maxVelocity = (this.mobile.matches ? 16 : 23) * this.profile.strength;
        this.velocity = Math.max(-maxVelocity, Math.min(maxVelocity, this.velocity + this.pendingVelocity));
        this.pendingVelocity = 0;
        this.kickTime = null;
      }
      const { decay, frequency } = this.profile;
      const envelope = Math.exp(-decay * dt);
      const c = Math.cos(frequency * dt);
      const s = Math.sin(frequency * dt);
      const a = this.angle;
      const b = (this.velocity + decay * a) / frequency;
      this.angle = envelope * (a * c + b * s);
      this.velocity = envelope * ((b * frequency - decay * a) * c - (a * frequency + decay * b) * s);
      const limit = this.profile.limit * (this.mobile.matches ? 0.72 : 1);
      if (Math.abs(this.angle) > limit) {
        this.angle = Math.sign(this.angle) * limit;
        this.velocity = 0;
      }
      if (this.kickTime === null && Math.abs(this.angle) < 0.025 && Math.abs(this.velocity) < 0.08) {
        this.stop();
        return;
      }
      const transform = `rotate(${this.angle.toFixed(4)}deg)`;
      if (transform !== this.lastTransform) {
        this.pendulum.style.setProperty('transform', transform);
        this.lastTransform = transform;
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    stop() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.pendingVelocity = 0;
      this.kickTime = null;
      this.angle = 0;
      this.velocity = 0;
      this.pendulum?.style.removeProperty('transform');
      this.lastTransform = '';
      this.removeAttribute('data-moving');
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('lyma-lamp-swing', LymaLampSwing);
})();
